import { OutlineLocationIcon } from "@App/Components/common/Icons";
import { Input } from "@App/Components/common/Input";
import { Button } from "@App/Components/Funnel/Button";
import { type FunctionComponent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

interface AddressModalProps {
  customer: {
    name: string;
    street: string;
    postcode: string;
    city: string;
    phone: string;
    email: string;
  };
  closeModal: () => void;
  submit: (
    name: string,
    phone: string,
    street: string,
    city: string,
    postcode: string,
    email: string,
  ) => void;
  translationKey?: string;
}

export const AddressModal: FunctionComponent<AddressModalProps> = ({
  customer,
  closeModal,
  submit,
  translationKey = "vehicleReturn",
}: AddressModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(customer.name);
  const [street, setStreet] = useState<string>(customer.street);
  const [postcode, setPostcode] = useState<string>(customer.postcode);
  const [city, setCity] = useState<string>(customer.city);
  const [phone, setPhone] = useState<string>(customer.phone);
  const [email, setEmail] = useState<string>(customer.email);

  // The contract data occasionally carries an unusable email, which the pickup mutation
  // rejects, so the customer corrects it here instead of us passing it through blindly.
  const isEmailValid = z.email().safeParse(email).success;
  const showEmailError = email.length > 0 && !isEmailValid;

  return (
    <div className="flex justify-center items-center absolute top-0 left-0 w-full h-screen">
      <div
        className="fixed top-0 left-0 w-full h-screen bg-neutral-700 opacity-75 z-20"
        onClick={closeModal}
      />
      <div className="w-full sm:w-[535px] bg-white rounded-lg shadow-lg p-5 m-3 z-30">
        <div className="text-center mb-5">
          <div className="inline-flex justify-center items-center rounded-full bg-neutral-100 py-4 px-5 mb-5">
            <OutlineLocationIcon className={"w-3"} />
          </div>
          <div className="text-xl font-bold font-serif mb-3">
            {t(`${translationKey}.addressModal.headline`)}
          </div>
          <p className={"text-sm text-neutral-500"}>
            <Trans i18nKey={`${translationKey}.addressModal.description`} />
          </p>
        </div>
        <div>
          <label htmlFor="name">{t(`${translationKey}.addressModal.name.label`)}</label>
          <Input
            id="name"
            type="text"
            className={"w-full mt-0 mb-3"}
            value={name}
            onChange={setName}
          />
        </div>
        <div>
          <label htmlFor="phone">{t(`${translationKey}.addressModal.phone.label`)}</label>
          <Input
            id="phone"
            type="tel"
            className={"w-full mt-0 mb-3"}
            value={phone}
            onChange={setPhone}
          />
        </div>
        <div>
          <label htmlFor="email">{t(`${translationKey}.addressModal.email.label`)}</label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            className={"w-full mt-0 mb-3"}
            value={email}
            onChange={setEmail}
            validationMessage={
              showEmailError ? t(`${translationKey}.addressModal.email.invalid`) : undefined
            }
          />
        </div>
        <div>
          <label htmlFor="street">{t(`${translationKey}.addressModal.street.label`)}</label>
          <Input
            id="street"
            type="text"
            className={"w-full mt-0 mb-3"}
            value={street}
            onChange={setStreet}
          />
        </div>
        <div>
          <label htmlFor="postcode">{t(`${translationKey}.addressModal.postcode.label`)}</label>
          <Input
            id="postcode"
            type="text"
            className={"w-1/3 mt-0 mb-3"}
            value={postcode}
            onChange={setPostcode}
          />
        </div>
        <div>
          <label htmlFor="city">{t(`${translationKey}.addressModal.city.label`)}</label>
          <Input
            id="city"
            type="text"
            className={"w-full mt-0 mb-3"}
            value={city}
            onChange={setCity}
          />
        </div>

        <div className="text-center mb-5">
          <p className={"text-sm text-neutral-500"}>{t(`${translationKey}.addressModal.hint`)}</p>
        </div>

        <div className="flex justify-between gap-3 mt-3">
          <Button
            title={t(`${translationKey}.addressModal.cancelButton.label`)}
            className={"md:!w-1/2"}
            outline
            onClick={closeModal}
          />
          <Button
            title={t(`${translationKey}.addressModal.acceptButton.label`)}
            className={"md:!w-1/2"}
            disabled={!isEmailValid}
            onClick={() => submit(name, phone, street, city, postcode, email)}
          />
        </div>
      </div>
    </div>
  );
};
